import {
  ArrowRight,
  BrainCircuit,
  BriefcaseBusiness,
  Check,
  ChevronDown,
  CircleGauge,
  Eye,
  Layers3,
  Menu,
  ShieldCheck,
  Sparkles,
  Target,
  X,
} from 'lucide-react';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { articles, type Article } from './insights';
import './craft.css';
import ImmersiveHero from './ImmersiveHero';
import ServiceArtwork from './ServiceArtwork';
import { usePageImmersion, DepthField, InsightArtwork, BoundarySculpture } from './PageImmersion';

const EMAIL = 'info@cisooperator.com';
const TURNSTILE_SITE_KEY = '0x4AAAAAAEZcbes1YsFdXogS';

type LeadService = 'general' | 'risk' | 'fractional' | 'ai';
type SubmitState = 'idle' | 'submitting' | 'error';

type TurnstileApi = {
  render: (container: HTMLElement, options: {
    sitekey: string;
    action?: string;
    theme?: 'light' | 'dark' | 'auto';
    size?: 'normal' | 'compact' | 'flexible';
    'error-callback'?: () => void;
    'refresh-expired'?: 'auto' | 'manual' | 'never';
  }) => string;
  reset: (widget?: string | HTMLElement) => void;
  remove: (widget: string) => void;
};

type BrowserIntegrations = Window & {
  gtag?: (...args: unknown[]) => void;
  turnstile?: TurnstileApi;
  cisoOperatorConversionReceipt?: string;
  cisoOperatorConversionEmitted?: boolean;
};

type LeadAttribution = {
  source: string;
  medium: string;
  campaign: string;
  term: string;
  content: string;
  gclid: string;
  gbraid: string;
  wbraid: string;
};

const ATTRIBUTION_STORAGE_KEY = 'ciso_operator_attribution';
const TURNSTILE_SCRIPT_ID = 'cloudflare-turnstile-api';
let turnstileLoadPromise: Promise<TurnstileApi> | null = null;

function loadTurnstileApi(): Promise<TurnstileApi> {
  const integrations = window as BrowserIntegrations;
  if (integrations.turnstile) return Promise.resolve(integrations.turnstile);
  if (turnstileLoadPromise) return turnstileLoadPromise;

  const pending = new Promise<TurnstileApi>((resolve, reject) => {
    const existing = document.getElementById(TURNSTILE_SCRIPT_ID) as HTMLScriptElement | null;
    const script = existing ?? document.createElement('script');

    const cleanup = () => {
      script.removeEventListener('load', onLoad);
      script.removeEventListener('error', onError);
    };
    const onLoad = () => {
      cleanup();
      const api = (window as BrowserIntegrations).turnstile;
      if (api) resolve(api);
      else {
        script.remove();
        reject(new Error('Cloudflare Turnstile loaded without exposing its API.'));
      }
    };
    const onError = () => {
      cleanup();
      script.remove();
      reject(new Error('Cloudflare Turnstile could not be loaded.'));
    };

    script.addEventListener('load', onLoad, { once: true });
    script.addEventListener('error', onError, { once: true });
    if (!existing) {
      script.id = TURNSTILE_SCRIPT_ID;
      script.async = true;
      script.defer = true;
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      document.head.appendChild(script);
    }
  });

  turnstileLoadPromise = pending;
  void pending.catch(() => {
    if (turnstileLoadPromise === pending) turnstileLoadPromise = null;
  });
  return pending;
}

function trackEvent(name: string, parameters: Record<string, unknown> = {}) {
  (window as BrowserIntegrations).gtag?.('event', name, parameters);
}

function attributionFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const current: LeadAttribution = {
    source: params.get('utm_source') || '',
    medium: params.get('utm_medium') || '',
    campaign: params.get('utm_campaign') || '',
    term: params.get('utm_term') || '',
    content: params.get('utm_content') || '',
    gclid: params.get('gclid') || '',
    gbraid: params.get('gbraid') || '',
    wbraid: params.get('wbraid') || '',
  };

  try {
    const stored = JSON.parse(sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY) || 'null') as LeadAttribution | null;
    const hasCurrentAttribution = Object.values(current).some(Boolean);
    if (!hasCurrentAttribution && stored) return stored;
    if (current.gclid || current.gbraid || current.wbraid) {
      current.source ||= 'google';
      current.medium ||= 'cpc';
    }
    if (hasCurrentAttribution) sessionStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(current));
  } catch {
    // Storage may be unavailable in privacy-focused browser modes. The live URL still provides attribution.
  }
  return current;
}

function safeReferrer(): string {
  if (!document.referrer) return '';
  try {
    const url = new URL(document.referrer);
    return `${url.origin}${url.pathname}`;
  } catch {
    return '';
  }
}

function LeadForm({ service, prompt }: { service: LeadService; prompt: string }) {
  const [state, setState] = useState<SubmitState>('idle');
  const [message, setMessage] = useState('');
  const [startedAt, setStartedAt] = useState(() => Date.now());
  const formStarted = useRef(false);
  const turnstileContainer = useRef<HTMLDivElement>(null);
  const turnstileWidgetId = useRef<string | null>(null);
  const submitLabel = service === 'risk'
    ? 'Request an assessment scope'
    : service === 'fractional'
      ? 'Discuss fractional CISO support'
      : service === 'ai'
        ? 'Discuss AI agent security'
        : 'Request a conversation';

  useEffect(() => {
    let disposed = false;
    if (['localhost', '127.0.0.1', '[::1]'].includes(location.hostname)) return;

    void loadTurnstileApi()
      .then((turnstile) => {
        if (disposed || !turnstileContainer.current) return;
        turnstileWidgetId.current = turnstile.render(turnstileContainer.current, {
          sitekey: TURNSTILE_SITE_KEY,
          action: 'lead_form',
          theme: 'light',
          size: 'flexible',
          'refresh-expired': 'auto',
          'error-callback': () => {
            if (disposed) return;
            setState('error');
            setMessage('Security verification could not be completed. Please refresh the page and try again.');
          },
        });
      })
      .catch(() => {
        if (disposed) return;
        setState('error');
        setMessage('Security verification could not be loaded. Please refresh the page and try again.');
      });

    return () => {
      disposed = true;
      const widgetId = turnstileWidgetId.current;
      const turnstile = (window as BrowserIntegrations).turnstile;
      if (widgetId && turnstile) turnstile.remove(widgetId);
      turnstileWidgetId.current = null;
    };
  }, []);

  function trackFormStart() {
    if (formStarted.current) return;
    formStarted.current = true;
    trackEvent('form_start', {
      form_name: `ciso_operator_${service}`,
      lead_type: service,
    });
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (['localhost', '127.0.0.1', '[::1]'].includes(location.hostname)) {
      setState('error');
      setMessage('Local preview only. Your details have not been sent. Form delivery requires the connected website backend.');
      return;
    }
    if (state === 'submitting') return;
    const form = event.currentTarget;
    const data = new FormData(form);
    const turnstileToken = String(data.get('cf-turnstile-response') || '');

    if (!turnstileToken) {
      setState('error');
      setMessage('Please complete the security verification and try again.');
      return;
    }

    setState('submitting');
    setMessage('');
    const submissionId = crypto.randomUUID();

    try {
      const response = await fetch('/api/lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          submissionId,
          service,
          name: data.get('name'),
          email: data.get('email'),
          company: data.get('company'),
          need: data.get('need'),
          website: data.get('website'),
          startedAt,
          turnstileToken,
          landingPath: window.location.pathname,
          referrer: safeReferrer(),
          attribution: attributionFromUrl(),
        }),
      });
      const result = await response.json() as { ok?: boolean; error?: string };
      if (!response.ok || !result.ok) throw new Error(result.error || 'We could not submit your request.');

      const successPath = service === 'risk'
        ? '/thank-you-risk-assessment'
        : service === 'fractional'
          ? '/thank-you-fractional-ciso'
          : service === 'ai'
            ? '/thank-you-ai-agent-security'
            : '/thank-you';
      window.location.assign(`${successPath}?receipt=${encodeURIComponent(submissionId)}`);
    } catch (error) {
      setState('error');
      setMessage(error instanceof Error ? error.message : 'We could not submit your request. Please try again.');
      setStartedAt(Date.now());
      const turnstile = (window as BrowserIntegrations).turnstile;
      if (turnstile && turnstileWidgetId.current) turnstile.reset(turnstileWidgetId.current);
    }
  }

  return (
    <form
      className="contact-form rr-block"
      id={`ciso_operator_${service}`}
      name={`ciso_operator_${service}`}
      onFocusCapture={trackFormStart}
      onSubmit={submit}
      method="post"
      action="/api/leads"
    >
      <div className="form-row">
        <label>Full name<input name="name" autoComplete="name" maxLength={120} required /></label>
        <label>Work email<input type="email" name="email" autoComplete="email" maxLength={254} required /></label>
      </div>
      <label>Company<input name="company" autoComplete="organization" maxLength={160} required /></label>
      <label>What prompted the conversation?<textarea name="need" rows={4} minLength={10} maxLength={3000} required placeholder={prompt} /></label>
      <label className="form-honeypot" aria-hidden="true">Website<input name="website" tabIndex={-1} autoComplete="off" /></label>
      <div className="turnstile-container" ref={turnstileContainer} role="group" aria-label="Security verification" />
      <button className="button button--teal" type="submit" disabled={state === 'submitting'}>
        {state === 'submitting' ? 'Submitting securely…' : submitLabel} <ArrowRight size={17} />
      </button>
      <p className="form-note">Submitted securely to CISO Operator. We will respond from {EMAIL}. Do not include passwords, vulnerability details, regulated records, or other sensitive information. See our <a href="/privacy">privacy policy</a>.</p>
      {message && <p className="form-status form-status--error" role="alert">{message}</p>}
    </form>
  );
}

function Logo({ inverse = false }: { inverse?: boolean }) {
  return (
    <a className={`logo ${inverse ? 'logo--inverse' : ''}`} href="/">
      <svg className="logo__mark" viewBox="0 0 52 44" aria-hidden="true">
        <path className="logo__c" d="M29 4H18C10.27 4 4 10.27 4 18v8c0 7.73 6.27 14 14 14h11l-7-7h-4a7 7 0 0 1-7-7v-8a7 7 0 0 1 7-7h4l7-7Z" />
        <path className="logo__path" d="m24 11 11 11-11 11h9l11-11L33 11h-9Z" />
        <rect className="logo__signal" x="43" y="19" width="6" height="6" rx="1" transform="rotate(45 46 22)" />
      </svg>
      <span className="logo__divider" aria-hidden="true" />
      <span className="logo__name"><span>CISO</span><strong>OPERATOR</strong></span>
    </a>
  );
}

const nav = [
  ['Services', '/#services'],
  ['How it works', '/#method'],
  ['AI defense', '/ai-agent-security'],
  ['About', '/#about'],
  ['Insights', '/insights'],
] as const;

function Header({ ctaLabel = 'Request a security call' }: { ctaLabel?: string }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && setOpen(false);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  return (
    <header className={`site-header ${scrolled ? 'site-header--scrolled' : ''}`}>
      <div className="nav-shell">
        <Logo inverse />
        <nav className="desktop-nav" aria-label="Primary navigation">
          {nav.map(([label, href]) => <a href={href} key={href}>{label}</a>)}
        </nav>
        <a className="button button--light nav-cta" href="#contact">{ctaLabel} <ArrowRight size={15} /></a>
        <button className="menu-button" type="button" aria-expanded={open} aria-controls="mobile-navigation" onClick={() => setOpen((value) => !value)}>
          <span className="sr-only">Toggle navigation</span>
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      <div id="mobile-navigation" className={`mobile-nav ${open ? 'mobile-nav--open' : ''}`}>
        {nav.map(([label, href]) => (
          <a href={href} key={href} onClick={() => setOpen(false)}>
            {label}<ArrowRight size={15} />
          </a>
        ))}
        <a className="button button--primary" href="#contact" onClick={() => setOpen(false)}>{ctaLabel}</a>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="craft-hero" data-sc-act="flow" aria-labelledby="hero-heading">
      <Header />
      <div className="shell craft-hero__layout">
        <div className="craft-hero__copy">
          <div className="craft-label">CYBERSECURITY LEADERSHIP / BUILT TO OPERATE</div>
          <h1 id="hero-heading">Board-level<br />judgment.<br /><em>Operator-level<br />execution.</em></h1>
          <p>Know which risks matter. Put the right protections in place. Give your business a security program it can actually run.</p>
          <div className="hero__actions">
            <a className="button button--teal" href="#contact">Request a security call <ArrowRight size={17} /></a>
            <a className="text-link text-link--inverse" href="#services">Explore services <ArrowRight size={16} /></a>
          </div>
        </div>
        <div className="craft-diagram" aria-label="From business risk to visible progress">
          <div className="craft-diagram__grid" data-sc-parallax="-0.5" aria-hidden="true" />
          <div className="craft-diagram__content" data-sc-parallax="-0.9">
            <div className="craft-label">THE OPERATING PATH</div>
            <div className="craft-diagram__start">Business risk</div>
            <ol>{['Assess', 'Prioritize', 'Implement', 'Operate', 'Measure'].map((item) => <li key={item}><span />{item}</li>)}</ol>
          </div>
          <div className="craft-diagram__result" data-sc-parallax="0.35"><Check size={19} /><span>Visible progress.<br /><strong>Accountable ownership.</strong></span></div>
        </div>
      </div>
      <div className="shell craft-hero__foot"><span>Fractional & virtual CISO services</span><span>Strategy → protection → progress</span></div>
    </section>
  );
}

const services = [
  {
    icon: Target,
    number: '01',
    title: 'Operator Risk Brief',
    text: 'An executive risk and governance review that turns scattered concerns into a prioritized 90-day plan, named owners, and a defensible 12-month roadmap.',
    meta: '2–4 week assessment',
    href: '/cybersecurity-risk-assessment',
  },
  {
    icon: BriefcaseBusiness,
    number: '02',
    title: 'Fractional CISO',
    text: 'Senior cybersecurity judgment, operating cadence, investment guidance, and executive reporting at the level of commitment your business actually needs.',
    meta: 'Advisory or managed',
    href: '/fractional-ciso',
  },
  {
    icon: ShieldCheck,
    number: '03',
    title: 'Managed Defense',
    text: 'Assessment-led email, identity, endpoint, monitoring, and response capabilities—implemented with clear provider roles and governed as part of your program.',
    meta: 'Evidence before products',
    href: '/#contact',
  },
  {
    icon: BrainCircuit,
    number: '04',
    title: 'Secure AI & Agent Defense',
    text: 'Adopt AI agents with bounded permissions, protected data, human control, observable actions, and defenses built for AI-amplified attacks and fraud.',
    meta: 'Governance + technical controls',
    href: '/ai-agent-security',
  },
] as const;

function Services() {
  return (
    <section className="section craft-services" id="services" data-sc-act="flow">
      <div className="shell">
        <div className="section-heading section-heading--split">
          <div><div className="eyebrow"><span /> Begin with what the evidence supports</div><h2>Senior judgment.<br /><em>Working protection.</em></h2></div>
          <p>Security products are plentiful. Accountable execution is scarce. We start by clarifying risk, then build and operate the capabilities the business can defend and sustain.</p>
        </div>
        <div className="craft-service-list">
          {services.map(({ icon: Icon, title, text, meta, href }) => (
            <article className="craft-service" key={title} data-immersion-reveal>
              <Icon size={28} strokeWidth={1.4} aria-hidden="true" />
              <div><h3><a href={href}>{title}</a></h3><span className="craft-service__meta">{meta}</span></div>
              <p>{text}</p>
              <a className="craft-service__go" href={href} aria-label={`Explore ${title}`}><ArrowRight size={23} /></a>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

const method = [
  ['Assess', 'Understand the business, its exposure, obligations, operating dependencies, and current evidence.'],
  ['Prioritize', 'Separate material decisions from noise; assign owners, sequence work, and cost the roadmap.'],
  ['Implement', 'Put the right controls and security capabilities into the real operating environment.'],
  ['Operate', 'Create a practical cadence for decisions, exceptions, escalation, remediation, and partner oversight.'],
  ['Measure', 'Give executives and boards a clear view of progress, unresolved risk, and the next decision.'],
] as const;


function Method() {
  const [active, setActive] = useState(0);
  const section = useRef<HTMLElement>(null);
  const manual = useRef(false);
  useEffect(() => {
    const media = matchMedia('(prefers-reduced-motion: reduce), (max-width: 800px)');
    let frame = 0;
    const update = () => {
      frame = 0;
      if (!section.current) return;
      const stage = section.current.querySelector('.craft-method__stage') as HTMLElement;
      const stageRect = stage.getBoundingClientRect();
      if (stageRect.bottom > 0 && stageRect.top < innerHeight && !media.matches) {
        stage.dataset.scVerifyState = `${stage.querySelector('h3')?.textContent}:${Math.round(stageRect.top)}:${Math.round(stageRect.bottom)}:${stage.style.getPropertyValue('--method-progress')}`;
      } else { delete stage.dataset.scVerifyState; }
      if (media.matches || manual.current) return;
      const rect = section.current.getBoundingClientRect();
      const travel = section.current.offsetHeight - innerHeight;
      stage.style.setProperty('--method-progress', String(Math.min(1, Math.max(0, (-rect.top + 110) / travel))));
      if (travel > 0 && rect.top <= 110 && rect.bottom >= innerHeight) {
        setActive(Math.min(4, Math.max(0, Math.floor((-rect.top + 110) / travel * 5))));
      }
    };
    const onScroll = () => { manual.current = false; if (!frame) frame = requestAnimationFrame(update); };
    addEventListener('scroll', onScroll, { passive: true });
    addEventListener('resize', onScroll);
    media.addEventListener('change', onScroll);
    update();
    return () => { removeEventListener('scroll', onScroll); removeEventListener('resize', onScroll); media.removeEventListener('change', onScroll); cancelAnimationFrame(frame); };
  }, []);
  return (
    <section className="craft-method" id="method" ref={section} aria-labelledby="method-title" data-sc-act="flow">
      <div className="craft-method__stage">
        <div className="shell">
          <div className="craft-method__heading"><div className="craft-label">THE CISO OPERATOR METHOD</div><h2 id="method-title">From open question<br /><em>to operating discipline.</em></h2></div>
          <div className="craft-method__layout">
            <noscript><link rel="stylesheet" href="/method-nojs.css" /><ol className="craft-method__fallback">{method.map(([title,text]) => <li key={title}><h3>{title}</h3><p>{text}</p></li>)}</ol></noscript><div className="craft-method__steps" aria-label="Explore the five stages">
              {method.map(([title], i) => <button key={title} type="button" className={active === i ? 'is-active' : ''} aria-pressed={active === i} aria-controls="method-output" onClick={() => { manual.current = true; setActive(i); }}><span className="craft-method__dot" /><span>{title}</span><ArrowRight size={20} /></button>)}
            </div>
            <div className="craft-method__output" id="method-output" aria-live="polite" aria-atomic="true">
              <div className="craft-label">The CISO Operator method</div>
              <h3 key={active}>{method[active][0]}</h3>
              <p>{method[active][1]}</p>
              <div className="craft-method__track" aria-hidden="true">{method.map(([title], i) => <span key={title} className={i <= active ? 'is-done' : ''} />)}</div>
              <span className="craft-method__note">Every engagement follows one visible path. The result is not another binder—it is a system your leaders and teams know how to run.</span><div className="craft-method__progress" aria-hidden="true" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function AiDefense() {
  const controls = ['Agent and AI-system inventory', 'Least-privilege tool access', 'Prompt-injection defenses', 'Human approval for consequential actions', 'Logging, limits, rollback, and kill switches'];
  return (
    <section data-sc-act="flow" className="section ai-section" id="ai-defense">
      <div className="shell ai-layout">
        <div className="ai-visual" data-sc-reveal="up" data-sc-reveal-at="0 0.2" aria-hidden="true">
          <div className="ai-visual__core"><BrainCircuit size={34} /><span>CONTROL</span></div>
          <span className="node node--one">IDENTITY</span><span className="node node--two">DATA</span><span className="node node--three">TOOLS</span><span className="node node--four">HUMAN</span>
          <svg viewBox="0 0 500 500"><circle cx="250" cy="250" r="170" /><circle cx="250" cy="250" r="105" /><path d="M250 80v95M420 250h-95M250 420v-95M80 250h95" /></svg>
        </div>
        <div className="ai-copy">
          <div className="eyebrow"><span /> Secure AI & agent defense</div>
          <h2>Move fast with AI.<br />Keep authority bounded.</h2>
          <p className="lead">AI agents do more than generate text. They access data, call tools, trigger workflows, and make decisions. That changes the security model.</p>
          <p>We help leaders put ownership, permissions, data boundaries, validation, monitoring, and human control around AI adoption—while strengthening the business against AI-enabled phishing, impersonation, and fraud.</p>
          <ul className="check-list">{controls.map((control) => <li key={control}><Check size={16} />{control}</li>)}</ul>
          <a className="text-link" href="#contact">Discuss your AI environment <ArrowRight size={16} /></a>
        </div>
      </div>
    </section>
  );
}

function Audience() {
  const items = [
    [BriefcaseBusiness, 'Growth-stage leaders', 'You need senior security ownership before a full-time CISO makes economic sense.'],
    [Layers3, 'Operationally complex businesses', 'Cloud, vendors, acquisitions, regulated data, or multiple entities have outgrown informal ownership.'],
    [ShieldCheck, 'Regulated & assurance-driven teams', 'Customer requirements, audits, contracts, or regulated data demand a defensible security program.'],
    [Sparkles, 'AI-forward organizations', 'Agent adoption is moving faster than governance, identity, data protection, and assurance.'],
  ] as const;
  return (
    <section data-sc-act="flow" className="section section--sand">
      <div className="shell">
        <div className="section-heading section-heading--center">
          <div className="eyebrow"><span /> Built for the decision-makers</div>
          <h2>When cybersecurity becomes<br />a business-operating issue.</h2>
        </div>
        <div className="audience-grid">
          {items.map(([Icon, title, text]) => <article key={title} data-immersion-reveal><Icon size={23} /><h3>{title}</h3><p>{text}</p></article>)}
        </div>
      </div>
    </section>
  );
}

function About() {
  return (
    <section data-sc-act="flow" className="section about" id="about">
      <div className="shell about-layout">
        <div className="about__statement">
          <div className="eyebrow eyebrow--inverse"><span /> Senior-led, team-supported</div>
          <blockquote>The right security leadership, backed by specialists who can help execute.</blockquote>
        </div>
        <div className="about__copy">
          <h2>The right expertise for the work.</h2>
          <p>CISO Operator delivers operator-led cybersecurity leadership through a scalable model. Every engagement has a named senior lead responsible for decisions, priorities, execution oversight, and visible progress.</p>
          <p>As needs expand, we can draw on certified security professionals and specialist providers across cloud and identity, email security, security operations, incident readiness, compliance, and AI and agent security. Roles, responsibilities, and provider relationships are defined for each engagement.</p>
          <div className="about__credentials">
            <span><Eye size={18} /> Named senior engagement lead</span>
            <span><ShieldCheck size={18} /> Certified specialist support</span>
          </div>
          <a className="text-link" href="#contact">Discuss the team for your needs <ArrowRight size={15} /></a>
        </div>
      </div>
    </section>
  );
}

function InsightsTeaser() {
  return (
    <section data-sc-act="flow" className="section section--light" id="insights">
      <div className="shell">
        <div className="section-heading section-heading--split">
          <div><div className="eyebrow"><span /> Operator insights</div><h2>Useful before<br />the first meeting.</h2></div>
          <p>Executive guidance and practical frameworks for making cybersecurity a working part of the business.</p>
        </div>
        <div className="insight-grid">
          {articles.map((article, index) => (
            <article key={article.slug} data-immersion-reveal>
              <InsightArtwork index={index} />
              <div className={`insight-card__visual insight-card__visual--${index + 1}`}><span>{article.category.toUpperCase()}</span><i /><b>0{index + 1}</b></div>
              <div className="insight-card__body"><span>{article.category.toUpperCase()}</span><h3>{article.title}</h3><p>{article.summary}</p><a className="text-link" href={`/insights/${article.slug}`}>Read the guide <ArrowRight size={15} /></a></div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function InsightsPage() {
  const page = useRef<HTMLDivElement>(null);
  usePageImmersion(page);
  usePageMetadata('Security Insights for Business Leaders | CISO Operator', 'Executive guidance on cybersecurity leadership, risk decisions, and AI security from CISO Operator.', '/insights');
  return (
    <div ref={page} className="legal-page insights-page insights-page--immersive">
      <div className="legal-nav shell"><Logo inverse /><a className="text-link" href="/">Return to site <ArrowRight size={15} /></a></div>
      <main>
        <section className="insights-scene" data-immersion-scene aria-labelledby="insights-heading">
          <DepthField />
          <div className="shell insights-scene__layout">
            <div className="insights-scene__copy">
              <div className="eyebrow"><span /> Operator insights</div>
              <h1 id="insights-heading">Useful before the first meeting.</h1>
              <p>Executive guidance and practical frameworks for making cybersecurity a working part of the business. No hype, no product pitches — the same thinking we bring into engagements.</p>
            </div>
            <InsightArtwork index={0} hero />
          </div>
          <div className="insights-scene__edge" aria-hidden="true" />
        </section>
        <div className="insights-gallery">
          <div className="shell insights-index">
            {articles.map((article, index) => (
              <article key={article.slug} data-immersion-reveal>
                <InsightArtwork index={index} />
                <div className="insights-index__copy">
                  <span className="insights-index__meta">{article.category} · {article.readMinutes} min read</span>
                  <h2><a href={`/insights/${article.slug}`}>{article.title}</a></h2>
                  <p>{article.summary}</p>
                  <a className="text-link" href={`/insights/${article.slug}`}>Read the guide <ArrowRight size={15} /></a>
                </div>
              </article>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function ArticlePage({ article }: { article: Article }) {
  usePageMetadata(article.metaTitle, article.metaDescription, `/insights/${article.slug}`);
  const page = useRef<HTMLDivElement>(null);
  usePageImmersion(page);
  const Body = article.Body;
  return (
    <div ref={page} className="legal-page article-page article-page--immersive">
      <div className="reading-progress" aria-hidden="true"><i /></div>
      <div className="legal-nav shell"><Logo inverse /><a className="text-link" href="/insights">All insights <ArrowRight size={15} /></a></div>
      <main className="article-content">
        <section className="article-cover" data-immersion-scene>
          <DepthField />
          <div className="shell article-cover__layout"><div className="article-cover__copy">
        <div className="eyebrow"><span /> {article.category}</div>
        <h1>{article.title}</h1>
        <p className="article-byline">By the CISO Operator team · Published {article.datePublished} · {article.readMinutes} min read</p>
          </div><InsightArtwork index={articles.indexOf(article)} hero /></div>
        </section>
        <div className="shell legal-content article-reading">
        <Body />
        <section className="article-faq" aria-label="Frequently asked questions">
          <h2>Common questions</h2>
          <div className="faq-list">
            {article.faqs.map(([question, answer], index) => (
              <details key={question} open={index === 0}><summary>{question}<ChevronDown size={18} /></summary><p>{answer}</p></details>
            ))}
          </div>
        </section>
        <div className="article-cta">
          <h2>Talk it through with an operator</h2>
          <p>If security leadership is becoming a business question for your company, a security conversation is the fastest way to see whether fractional leadership fits.</p>
          <a className="button button--teal" href="/#contact">Request a security call <ArrowRight size={17} /></a>
        </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Contact() {
  return (
    <section data-sc-act="flow" className="section contact" id="contact">
      <div className="contact__field" aria-hidden="true"><i /><i /><i /></div>
      <div className="shell contact-layout">
        <div className="contact__copy">
          <div className="eyebrow eyebrow--inverse"><span /> Start with the decision</div>
          <h2>What security question is your business carrying?</h2>
          <p>Tell us what changed, what is at stake, and who needs clarity. The first conversation is designed to determine whether an Operator Risk Brief—or another path—makes sense.</p>
          <a href={`mailto:${EMAIL}`}>{EMAIL} <ArrowRight size={16} /></a>
          <p className="contact__secure">Need to reach us about something sensitive? See <a href="/secure-contact">Secure Contact</a>.</p>
        </div>
        <LeadForm service="general" prompt="A board request, customer diligence, AI rollout, assessment finding…" />
      </div>
    </section>
  );
}

type LandingKind = 'risk' | 'fractional' | 'ai';

const landingContent = {
  risk: {
    path: '/cybersecurity-risk-assessment',
    title: 'Cybersecurity Risk Assessment Services | CISO Operator',
    description: 'Cybersecurity risk assessment services that turn business exposure into a prioritized 90-day plan, named owners, and a practical 12-month roadmap.',
    eyebrow: 'Operator Risk Brief · Cybersecurity risk assessment',
    heading: <>A cybersecurity risk assessment that shows <em>what to do next.</em></>,
    lead: 'A focused executive and operating assessment that turns scattered security concerns into prioritized decisions, named owners, and an actionable roadmap.',
    primaryCta: 'Request an assessment scope',
    secondaryCta: 'See what you receive',
    secondaryHref: '#deliverables',
    meta: [['Typical engagement', '2–4 weeks'], ['Starting point', 'Business context + evidence'], ['Primary outcome', 'Prioritized action plan']],
    triggerHeading: 'When a security question becomes a business decision.',
    triggerIntro: 'The Operator Risk Brief is designed for moments when leaders need a defensible view of exposure, priorities, ownership, and investment—not another unranked list of findings.',
    triggers: [
      [BriefcaseBusiness, 'Customer or board pressure', 'A customer questionnaire, board request, insurer, or commercial opportunity requires a clear security position.'],
      [Layers3, 'Complexity has outgrown ownership', 'Cloud services, vendors, sensitive data, acquisitions, or multiple teams have created fragmented accountability.'],
      [ShieldCheck, 'A finding needs a decision', 'An incident, assessment, audit observation, or recurring control problem exposed uncertainty about what matters first.'],
      [BrainCircuit, 'AI is moving ahead of governance', 'AI tools or agents are accessing data and workflows before permissions, monitoring, and human control are defined.'],
    ],
    deliverableHeading: 'A decision-ready brief—not a binder.',
    deliverableIntro: 'The assessment connects business exposure to a practical operating sequence. Scope is confirmed before work begins and reflects the evidence available in your environment.',
    deliverables: [
      ['Executive risk view', 'Material risk scenarios explained in business language, including the decisions they create.'],
      ['Current and target profile', 'A structured view of current practices and the level of capability the business actually needs.'],
      ['Ownership map', 'Named decision owners, operating responsibilities, dependencies, and escalation points.'],
      ['90-day priority plan', 'Sequenced actions that distinguish urgent decisions from work that can wait.'],
      ['12-month roadmap', 'A sustainable path for governance, controls, capabilities, and measurable progress.'],
      ['Executive readout', 'A working session to align leaders on priorities, tradeoffs, and the next operating step.'],
    ],
    process: [
      ['Frame', 'Confirm the business trigger, scope, stakeholders, systems, obligations, and available evidence.'],
      ['Assess', 'Review governance, risk, identity, data, technology, resilience, vendors, and relevant AI use.'],
      ['Prioritize', 'Rank decisions by business impact, likelihood, dependency, effort, and urgency.'],
      ['Mobilize', 'Deliver the brief, align owners, and define the first operating cadence.'],
    ],
    fit: ['You need an independent view before buying more tools.', 'Leadership needs priorities, ownership, and investment choices.', 'Your teams can provide reasonable access to stakeholders and evidence.', 'You want a practical path into implementation or ongoing leadership.'],
    notFit: ['A penetration test, certification, or formal audit opinion.', 'A guarantee that all cybersecurity risk will be eliminated.', 'A generic checklist with no business or operating context.', 'An undisclosed product-reselling exercise.'],
    faqs: [
      ['What is a cybersecurity risk assessment?', 'A cybersecurity risk assessment identifies the business scenarios, systems, data, dependencies, and control gaps most likely to create material harm. It connects evidence to likelihood, impact, ownership, and treatment decisions rather than producing an unranked checklist.'],
      ['How do you perform a cybersecurity risk assessment?', 'We frame the business context and scope, review available evidence and stakeholder input, analyze material risk scenarios, prioritize treatment decisions, and translate the results into named owners, a 90-day action plan, and a 12-month roadmap.'],
      ['Is this a penetration test or compliance audit?', 'No. The Operator Risk Brief is an executive risk and governance assessment. Technical testing or formal audit work is separately scoped when needed and performed only by appropriately qualified resources.'],
      ['What information will you need?', 'The exact request depends on scope. It commonly includes stakeholder interviews and available policies, diagrams, inventories, prior findings, contracts, incident information, and operating evidence. Sensitive information should be shared only through an agreed secure channel.'],
      ['Will you recommend security products?', 'Only when the assessment supports a specific risk-treatment decision. Recommendations explain the business need, required capability, ownership, and provider role before a product is selected.'],
      ['What happens after the assessment?', 'You may execute the roadmap internally, engage CISO Operator for fractional leadership, or scope specific implementation and managed-defense work. The assessment does not require a follow-on retainer.'],
    ],
    emailSubject: 'CISO Operator — Operator Risk Brief inquiry',
    prompt: 'What prompted this assessment, and when do you need a decision?',
  },
  fractional: {
    path: '/fractional-ciso',
    title: 'Fractional CISO & vCISO Services | CISO Operator',
    description: 'Fractional CISO and vCISO services providing senior cybersecurity leadership, operating cadence, investment guidance, and accountable execution.',
    eyebrow: 'Fractional CISO services · Advisory or managed',
    heading: <>Fractional CISO leadership that <em>stays with the work.</em></>,
    lead: 'Get accountable cybersecurity leadership at the level of commitment your business needs—from executive guidance to active program management—without a full-time executive hire.',
    primaryCta: 'Discuss fractional CISO support',
    secondaryCta: 'Compare engagement levels',
    secondaryHref: '#engagement-levels',
    meta: [['Leadership model', 'Named senior lead'], ['Engagement level', 'Advisory or managed'], ['Support model', 'Certified specialists as needed']],
    triggerHeading: 'When cybersecurity needs an owner—not just advice.',
    triggerIntro: 'Fractional CISO support fits organizations where security decisions now cross leadership, technology, customers, vendors, finance, and operations, but a full-time CISO is not yet the right investment.',
    triggers: [
      [BriefcaseBusiness, 'No executive security owner', 'IT, engineering, compliance, and leadership are carrying pieces of security without one accountable operating lead.'],
      [CircleGauge, 'Progress is hard to see', 'Leaders receive activity reports, but priorities, unresolved decisions, and material risk remain unclear.'],
      [Layers3, 'Execution crosses many teams', 'Roadmap work depends on internal teams, external providers, customer commitments, and competing investments.'],
      [BrainCircuit, 'New technology changes exposure', 'Cloud transformation, AI adoption, transactions, or rapid growth require senior judgment and coordinated controls.'],
    ],
    deliverableHeading: 'Leadership matched to your operating reality.',
    deliverableIntro: 'The engagement is sized around the decisions, cadence, and execution ownership your business requires. Every model has a named senior lead; specialist support is added only when the scope justifies it.',
    deliverables: [
      ['Executive risk leadership', 'Translate technical exposure into business decisions, tradeoffs, and accountable action.'],
      ['Security operating cadence', 'Establish recurring decisions, remediation tracking, escalation, and exception management.'],
      ['Roadmap and investment guidance', 'Sequence initiatives and security spending around business priorities and dependencies.'],
      ['Executive and board reporting', 'Make material risk, progress, decisions, and residual exposure visible without dashboard theater.'],
      ['Customer and vendor coordination', 'Support security diligence, commitments, provider accountability, and cross-functional follow-through.'],
      ['Capability oversight', 'Govern assessment-backed email, identity, endpoint, monitoring, response, cloud, and AI-security work.'],
    ],
    process: [
      ['Define', 'Confirm the business mandate, authority, stakeholders, required cadence, and success measures.'],
      ['Baseline', 'Establish the risk, roadmap, evidence, provider, and operating context the engagement will inherit.'],
      ['Operate', 'Run the agreed advisory or management cadence and coordinate accountable execution.'],
      ['Report', 'Keep executives informed about progress, unresolved risk, exceptions, and required decisions.'],
    ],
    fit: ['You need senior ownership before a full-time CISO is economical.', 'Leadership will give the role clear access and decision pathways.', 'Internal teams or providers can execute assigned work.', 'You want a repeatable operating system, not periodic presentations.'],
    notFit: ['A 24/7 monitoring center delivered directly by CISO Operator.', 'A ceremonial title without access, authority, or operating participation.', 'A promise that one person will personally perform every specialist function.', 'Guaranteed compliance, certification, or elimination of all risk.'],
    faqs: [
      ['What does a fractional CISO do?', 'A fractional CISO provides part-time executive security leadership: setting priorities, advising executives, governing risk decisions, coordinating internal teams and providers, and making progress and unresolved exposure visible.'],
      ['What is the difference between a virtual CISO and a fractional CISO?', 'Both terms describe flexible access to senior security leadership. Virtual CISO, or vCISO, emphasizes remote delivery; fractional CISO emphasizes a part-time share of an executive role. The important distinction is the agreed authority, cadence, and execution responsibility.'],
      ['What is the difference between advisory and managed support?', 'In an advisory model, your team executes while the CISO Operator lead guides decisions, roadmap, policy, architecture, and reporting. In a managed model, the lead also runs the agreed program cadence, tracking, coordination, and governance.'],
      ['Who will perform the work?', 'Every engagement has a named senior cybersecurity lead. Certified security professionals or specialist providers may support defined areas when required, with roles and responsibilities stated for the engagement.'],
      ['Can you work with our existing MSP or security vendors?', 'Yes. The role can include clarifying responsibilities, reviewing evidence, aligning providers to the roadmap, and escalating gaps. Existing providers are not automatically replaced.'],
      ['Do you provide 24/7 monitoring and incident response?', 'CISO Operator can help architect, implement, and govern those capabilities. If a specialist provider performs continuous monitoring or response, that responsibility is identified clearly in the engagement.'],
    ],
    emailSubject: 'CISO Operator — fractional CISO inquiry',
    prompt: 'A leadership gap, board request, customer requirement, transformation, AI rollout…',
  },
  ai: {
    path: '/ai-agent-security',
    title: 'AI Agent Security Services & Risk Assessment | CISO Operator',
    description: 'Secure AI agents with bounded access, protected data, human approval, observable actions, and practical AI risk assessment and governance.',
    eyebrow: 'AI agent security · Governance + technical controls',
    heading: <>Secure AI agents before they inherit <em>business authority.</em></>,
    lead: 'Assess and secure the identities, data, tools, decisions, and workflows connected to AI agents—while preparing the business to defend against AI-amplified cyber threats.',
    primaryCta: 'Discuss an AI security review',
    secondaryCta: 'See the control areas',
    secondaryHref: '#deliverables',
    meta: [['Starting point', 'AI use + agent inventory'], ['Control model', 'Bounded authority'], ['Primary outcome', 'Prioritized safeguards']],
    triggerHeading: 'When AI adoption moves faster than security ownership.',
    triggerIntro: 'AI agent security becomes a business issue when autonomous or semi-autonomous systems can access sensitive data, use tools, initiate actions, or influence consequential decisions.',
    triggers: [
      [BrainCircuit, 'Agents can take action', 'AI systems can call tools, modify records, send messages, trigger workflows, or make recommendations that affect customers and operations.'],
      [Layers3, 'Data access is expanding', 'Models and agents are connected to internal documents, customer information, code, credentials, or business systems without a complete access model.'],
      [ShieldCheck, 'Threats are becoming AI-amplified', 'Attackers use AI to scale social engineering, fraud, reconnaissance, impersonation, and exploitation while defenders face new agent-specific attack paths.'],
      [BriefcaseBusiness, 'Governance is unclear', 'Leaders need a practical way to approve use cases, assign accountability, monitor behavior, and stop unsafe activity without freezing innovation.'],
    ],
    deliverableHeading: 'A control model for AI systems that can act.',
    deliverableIntro: 'The review connects AI use cases to business impact, technical authority, attack paths, and operating safeguards. Recommendations are prioritized around the systems and decisions that matter most.',
    deliverables: [
      ['AI and agent inventory', 'Identify material AI use cases, owners, models, agents, connected data, tools, identities, and business workflows.'],
      ['AI risk assessment', 'Analyze misuse, prompt injection, excessive agency, data exposure, identity abuse, supply-chain, fraud, and resilience scenarios.'],
      ['Identity and access boundaries', 'Define least-privilege identities, scoped credentials, tool permissions, data access, and separation between agents and human users.'],
      ['Action controls', 'Apply human approval, transaction limits, policy enforcement, rollback, and kill switches to consequential actions.'],
      ['Monitoring and evidence', 'Establish logging, traceability, anomaly detection, testing, exception handling, and evidence for management oversight.'],
      ['Prioritized implementation plan', 'Sequence immediate safeguards, engineering work, governance decisions, provider roles, and measurable operating milestones.'],
    ],
    process: [
      ['Discover', 'Map AI use cases, agents, models, owners, data, tools, decisions, and external dependencies.'],
      ['Assess', 'Evaluate business impact, threat scenarios, permissions, control gaps, monitoring, and recovery options.'],
      ['Bound', 'Define the technical and human controls that limit authority and protect sensitive information.'],
      ['Operate', 'Implement a practical approval, testing, monitoring, exception, and incident-response cadence.'],
    ],
    fit: ['AI agents access business data or tools.', 'Leadership needs a defensible AI risk view before scaling adoption.', 'Engineering teams need practical security requirements and priorities.', 'You want governance connected to technical controls and operating evidence.'],
    notFit: ['A guarantee that an AI system is risk-free.', 'Model-performance testing with no security or business-risk scope.', 'A policy document disconnected from implementation.', 'Unbounded autonomous deployment without human accountability.'],
    faqs: [
      ['How do I secure AI agent access?', 'Give each agent a distinct identity, apply least privilege, scope credentials and tools to approved tasks, separate sensitive environments, require human approval for consequential actions, and monitor every material action with the ability to revoke access quickly.'],
      ['How secure are AI agents?', 'Security depends on what an agent can access and do, how inputs and tool calls are controlled, and whether activity is observable and reversible. An agent with broad permissions and weak oversight can create substantially more risk than a standalone chatbot.'],
      ['How do you perform an AI risk assessment?', 'We inventory the use case and authority, map data and tool access, analyze misuse and attack scenarios, test the control design, identify accountable owners, and prioritize safeguards based on business impact and implementation dependency.'],
      ['Does this include protection from AI-powered cyber threats?', 'Yes. The review can address AI-enabled phishing, impersonation, fraud, reconnaissance, malicious automation, and attacks against AI systems, alongside the controls needed to secure the business use of AI.'],
      ['Can CISO Operator help implement the controls?', 'Yes. After the assessment, implementation can be scoped for identity, access, monitoring, workflow approvals, logging, testing, incident readiness, and specialist technology where the evidence supports it.'],
    ],
    emailSubject: 'CISO Operator — AI agent security inquiry',
    prompt: 'An AI rollout, agent deployment, data-access concern, board question, customer requirement…',
  },
} as const;

function usePageMetadata(title: string, description: string, path: string) {
  useEffect(() => {
    document.title = title;
    document.querySelector('meta[name="description"]')?.setAttribute('content', description);
    document.querySelector('meta[property="og:title"]')?.setAttribute('content', title);
    document.querySelector('meta[property="og:description"]')?.setAttribute('content', description);
    document.querySelector('meta[property="og:url"]')?.setAttribute('content', `https://cisooperator.com${path}`);
    document.querySelector('meta[name="twitter:title"]')?.setAttribute('content', title);
    document.querySelector('meta[name="twitter:description"]')?.setAttribute('content', description);
    document.querySelector('link[rel="canonical"]')?.setAttribute('href', `https://cisooperator.com${path}`);
  }, [description, path, title]);
}

function LandingContact({ kind }: { kind: LandingKind }) {
  const content = landingContent[kind];
  const service: LeadService = kind;

  return (
    <section className="section landing-contact" id="contact">
      <div className="shell landing-contact__layout">
        <div>
          <div className="eyebrow eyebrow--inverse"><span /> Start with a focused conversation</div>
          <h2>{kind === 'risk' ? 'What changed—and what decision is waiting?' : kind === 'ai' ? 'What can the AI system access—and what can it do?' : 'Where does security ownership break down today?'}</h2>
          <p>Share the business trigger and the outcome leadership needs. We will use the first conversation to determine fit, define the next step, and identify whether a different path would serve you better.</p>
          <a href={`mailto:${EMAIL}`}>{EMAIL} <ArrowRight size={16} /></a>
        </div>
        <LeadForm service={service} prompt={content.prompt} />
      </div>
    </section>
  );
}

function ThankYouPage({ service }: { service: LeadService }) {
  const copy = service === 'risk'
    ? { title: 'Assessment request received', text: 'Thank you. Your Operator Risk Brief request was submitted securely. We will review the business trigger and respond using the work email you provided.' }
    : service === 'fractional'
      ? { title: 'Fractional CISO request received', text: 'Thank you. Your request was submitted securely. We will review the ownership gap and respond using the work email you provided.' }
      : service === 'ai'
        ? { title: 'AI agent security request received', text: 'Thank you. Your request was submitted securely. We will review the AI use case, access concerns, and business decision you described, then respond using the work email you provided.' }
        : { title: 'Your request was received', text: 'Thank you. Your inquiry was submitted securely. We will review it and respond using the work email you provided.' };

  useEffect(() => {
    document.title = `${copy.title} | CISO Operator`;
    document.querySelector('meta[name="robots"]')?.setAttribute('content', 'noindex,nofollow');
    document.querySelector('link[rel="canonical"]')?.setAttribute('href', 'https://cisooperator.com/');

    const integrations = window as BrowserIntegrations;
    const receipt = integrations.cisoOperatorConversionReceipt || '';
    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(receipt)) return;
    const eventKey = `ciso_operator_conversion_${receipt}`;
    if (integrations.cisoOperatorConversionEmitted) return;
    try {
      if (sessionStorage.getItem(eventKey)) return;
    } catch {
      // Continue without browser-side deduplication if storage is blocked.
    }

    integrations.cisoOperatorConversionEmitted = true;
    try {
      sessionStorage.setItem(eventKey, 'sent');
    } catch {
      // The server enforces the one-time receipt when storage is unavailable.
    }

    trackEvent('page_view', {
      page_location: `${window.location.origin}${window.location.pathname}`,
      page_path: window.location.pathname,
      page_title: document.title,
    });

    const parameters = {
      lead_type: service,
      form_id: `ciso_operator_${service}`,
      form_name: `ciso_operator_${service}`,
      transaction_id: receipt,
      transport_type: 'beacon',
    };
    trackEvent('generate_lead', parameters);
  }, [copy.title, service]);

  return (
    <div className="confirmation-page">
      <Header />
      <main className="confirmation-card">
        <div className="confirmation-card__mark"><Check size={32} /></div>
        <div className="eyebrow"><span /> Secure submission confirmed</div>
        <h1>{copy.title}</h1>
        <p>{copy.text}</p>
        <p className="confirmation-card__safety">Please do not email passwords, authentication secrets, regulated records, or detailed vulnerability information. We will establish a secure channel if the conversation requires sensitive material.</p>
        <a className="button button--teal" href="/">Return to CISO Operator <ArrowRight size={17} /></a>
      </main>
    </div>
  );
}

function ServiceOfferEvidence({ kind }: { kind: 'risk' | 'fractional' }) {
  return (
    <section className="section service-offer" aria-label="Engagement scope and leadership">
      <div className="shell">
        <div className="service-offer__intro">
          <div>
            <div className="eyebrow"><span /> Clear scope before commitment</div>
            <h2>{kind === 'risk' ? 'Know what you are buying.' : 'Choose the leadership your team needs.'}</h2>
            <p>{kind === 'risk'
              ? 'For business leaders responding to customer, board, or insurer security questions. Your assessment combines stakeholder interviews and evidence review with an executive readout, named owners, a 90-day plan, and a 12-month roadmap.'
              : 'A named senior lead connects business risk to decisions, priorities, and a recurring operating cadence. The level of support determines who guides the work and who coordinates execution.'}</p>
            <p>{kind === 'risk'
              ? 'Typically 2–4 weeks with timely access to your team and evidence. Technical testing, certification, formal audit opinions, and remediation require separate scope.'
              : 'An Operator Risk Brief or accepted equivalent baseline normally precedes the engagement. Where a credible baseline exists, paid onboarding and validation may be used instead.'}</p>
          </div>
          <aside data-immersion-reveal className="service-practitioner" aria-label="Engagement leadership">
            <div className="eyebrow"><span /> Engagement leadership</div>
            <h3>Experienced security leadership</h3>
            <p className="service-practitioner__role">Certified security professionals</p>
            <p>Your engagement brings experienced security leadership and certified security expertise to enterprise infrastructure, identity and access management, cloud security, and executive security decisions.</p>
            <p>Your proposal names the engagement lead and defines the responsibilities of any supporting specialists.</p>
          </aside>
        </div>
        {kind === 'risk' ? (
          <div className="service-example" id="sample-risk-brief" data-immersion-reveal>
            <div className="eyebrow"><span /> Illustrative deliverable · not a client result</div>
            <h3>From a risk scenario to an owned action</h3>
            <dl className="service-example__grid">
              <div><dt>Risk scenario</dt><dd>A departed employee retains privileged access to a business system.</dd></div>
              <div><dt>Business impact</dt><dd>Customer information or critical operations could be exposed to unauthorized changes.</dd></div>
              <div><dt>Proposed owner</dt><dd>The client’s IT lead, with an executive sponsor for decisions and exceptions.</dd></div>
              <div><dt>Next action</dt><dd>Review privileged accounts, remove unneeded access through approved change controls, and retain evidence of the review.</dd></div>
            </dl>
            <p>Actual findings and priorities depend on the evidence and scope of your assessment.</p>
          </div>
        ) : (
          <div id="engagement-levels" className="service-tiers">
            <h3>Compare engagement levels</h3>
            <div className="service-tiers__grid">
              <article data-immersion-reveal><h4>Advisory</h4><p className="service-tier-price">From $3,500/month</p><p>Your team executes; the senior lead guides priorities, roadmap, policy, investment, and provider decisions.</p><p>Monthly executive guidance and a quarterly plan refresh. Normally a three-month initial term.</p></article>
              <article data-immersion-reveal><h4>Managed</h4><p className="service-tier-price">From $6,500/month</p><p>The senior lead also runs the agreed program cadence, action tracking, coordination, and executive reporting.</p><p>Biweekly operating cadence. Normally a three- to six-month initial term.</p></article>
              <article data-immersion-reveal><h4>Embedded</h4><p className="service-tier-price">From $12,500/month</p><p>Weekly senior leadership for complex change, transactions, remediation, and a higher executive decision load.</p><p>A six-month initial term is commonly recommended for complex change.</p></article>
            </div>
            <p>Starting prices reflect a defined scope. Final fees, meeting allowance, entities, provider costs, implementation responsibilities, and support boundaries are set in writing.</p>
            <div className="service-example"><div className="eyebrow"><span /> Illustrative reporting format · not a client result</div><h4>An executive update that supports a decision</h4><p>Material risk → accountable owner → action and evidence → unresolved decision → next review date. Your engagement defines the actual measures and reporting cadence.</p></div>
          </div>
        )}
      </div>
    </section>
  );
}

const relatedReading: Record<LandingKind, readonly string[]> = {
  risk: ['cmmc-phase-2-suspended', 'incident-response-plan', 'third-party-risk-management'],
  fractional: ['what-is-a-fractional-ciso', 'what-is-a-vciso', 'incident-response-plan'],
  ai: ['prompt-injection-business-guide', 'shadow-ai-at-work', 'pacing-the-frontier-cybersecurity'],
};

function RelatedReading({ kind }: { kind: LandingKind }) {
  const items = relatedReading[kind]
    .map((slug) => articles.find((entry) => entry.slug === slug))
    .filter((entry): entry is Article => Boolean(entry));
  if (!items.length) return null;
  return (
    <section className="section landing-reading" aria-labelledby={`reading-heading-${kind}`}>
      <div className="shell">
        <div className="section-heading section-heading--split">
          <div><div className="eyebrow"><span /> Related reading</div><h2 id={`reading-heading-${kind}`}>Guides for the decision in front of you.</h2></div>
          <p>Plain-language guides from the same operating playbook, written for leaders deciding what to do next.</p>
        </div>
        <div className="landing-reading__list">
          {items.map((article) => (
            <article key={article.slug} data-immersion-reveal>
              <span>{article.category}</span>
              <h3><a href={`/insights/${article.slug}`}>{article.title}</a></h3>
              <p>{article.summary}</p>
              <a className="text-link" href={`/insights/${article.slug}`} aria-label={`Read the guide: ${article.title}`}>Read the guide <ArrowRight size={15} /></a>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function ServiceLandingPage({ kind }: { kind: LandingKind }) {
  const content = landingContent[kind];
  const page = useRef<HTMLDivElement>(null);
  usePageImmersion(page);
  usePageMetadata(content.title, content.description, content.path);

  return (
    <div ref={page} className={`landing-page${kind === 'ai' ? ' ai-security-page' : ` service-page--immersive service-page--${kind}`}`}>
      <Header ctaLabel={kind === 'ai' ? undefined : content.primaryCta} />
      <section className="landing-hero" data-immersion-scene aria-labelledby="landing-heading">
        <DepthField />
        <div className="shell landing-hero__content">
          <a className="landing-back" href="/"><ArrowRight size={14} /> CISO Operator</a>
          <div className="eyebrow eyebrow--inverse"><span /> {content.eyebrow}</div>
          <h1 id="landing-heading">{content.heading}</h1>
          <p>{content.lead}</p>
          {kind === 'risk' && <p className="landing-price"><strong>Essential scope from $6,500.</strong> Final scope and fee confirmed in writing.</p>}
          {kind === 'fractional' && <p className="landing-price"><strong>Advisory from $3,500/month.</strong> Choose the level of leadership and execution your business needs.</p>}
          {kind === 'ai' ? <BoundarySculpture hero /> : <ServiceArtwork kind={kind} />}
          <div className="hero__actions">
            <a className="button button--teal" href="#contact" onClick={() => trackEvent('select_content', { content_type: 'cta', item_id: `${kind}_hero_contact` })}>{content.primaryCta} <ArrowRight size={17} /></a>
            <a className="text-link text-link--inverse" href={content.secondaryHref} onClick={() => trackEvent('select_content', { content_type: 'cta', item_id: `${kind}_hero_deliverables` })}>{content.secondaryCta} <ChevronDown size={16} /></a>
          </div>
        </div>
        <div className="shell landing-meta">
          {content.meta.map(([label, value]) => <div key={label}><span>{label}</span><strong>{value}</strong></div>)}
        </div>
      </section>

      <main>
        {kind !== 'ai' && <ServiceOfferEvidence kind={kind} />}
        <section className="section landing-triggers">
          <div className="shell">
            <div className="section-heading section-heading--split">
              <div><div className="eyebrow"><span /> The buying moment</div><h2>{content.triggerHeading}</h2></div>
              <p>{content.triggerIntro}</p>
            </div>
            <div className="landing-card-grid">
              {content.triggers.map(([Icon, title, text], index) => (
                <article key={title} data-immersion-reveal><div><span>0{index + 1}</span><Icon size={22} /></div><h3>{title}</h3><p>{text}</p></article>
              ))}
            </div>
          </div>
        </section>

        <section className="section section--navy landing-deliverables" id="deliverables">
          <div className="shell">
            <div className="section-heading section-heading--split section-heading--inverse">
              <div><div className="eyebrow eyebrow--inverse"><span /> What the engagement creates</div><h2>{content.deliverableHeading}</h2></div>
              <p>{content.deliverableIntro}</p>
            </div>
            {kind === 'ai' && <div className="control-display"><BoundarySculpture /></div>}
            <div className="deliverable-grid">
              {content.deliverables.map(([title, text], index) => (
                <article key={title} data-immersion-reveal><span>0{index + 1}</span><Check size={18} /><h3>{title}</h3><p>{text}</p></article>
              ))}
            </div>
          </div>
        </section>

        <section className="section landing-process">
          <div className="shell landing-process__layout">
            <div className="landing-process__intro"><div className="eyebrow"><span /> A visible operating path</div><h2>Clear from first question to next action.</h2><p>Scope and responsibilities are agreed before work begins. The process is designed to expose decisions early and leave the business with an operating path it can sustain.</p></div>
            <ol>
              {content.process.map(([title, text], index) => <li key={title} data-immersion-reveal><span>0{index + 1}</span><div><h3>{title}</h3><p>{text}</p></div></li>)}
            </ol>
          </div>
        </section>

        <section className="section section--sand landing-fit">
          <div className="shell">
            <div className="section-heading section-heading--center"><div className="eyebrow"><span /> Fit before commitment</div><h2>A useful engagement has clear boundaries.</h2></div>
            <div className="fit-grid">
              <article><span className="fit-label fit-label--yes">Strong fit</span><ul>{content.fit.map((item) => <li key={item}><Check size={17} />{item}</li>)}</ul></article>
              <article><span className="fit-label">Not this engagement</span><ul>{content.notFit.map((item) => <li key={item}><X size={17} />{item}</li>)}</ul></article>
            </div>
          </div>
        </section>

        <section className="section faq landing-faq">
          <div className="shell faq-layout">
            <div><div className="eyebrow"><span /> Common questions</div><h2>Clarity before commitment.</h2></div>
            <div className="faq-list">
              {content.faqs.map(([question, answer], index) => <details key={question} open={index === 0}><summary>{question}<ChevronDown size={18} /></summary><p>{answer}</p></details>)}
            </div>
          </div>
        </section>

        <RelatedReading kind={kind} />

        <LandingContact kind={kind} />
      </main>
      <Footer />
    </div>
  );
}

const faqs = [
  ['What is cybersecurity consulting?', 'Cybersecurity consulting helps a business understand material cyber risk, make defensible security decisions, design the right controls, and organize accountable implementation. CISO Operator connects that advice to an operating roadmap instead of stopping at recommendations.'],
  ['What is a fractional CISO or vCISO?', 'A fractional CISO or virtual CISO is a senior cybersecurity leader who works with your business on a flexible basis. The role can range from executive advisory to hands-on program leadership, without the commitment of a full-time executive hire.'],
  ['What cybersecurity services does a growing business need first?', 'The right starting point depends on exposure and business priorities. Common foundations include identity and access, business email security, endpoint protection, monitoring and response, recovery readiness, vendor risk, policies, and a clear ownership and reporting cadence.'],
  ['Do you start by selling security tools?', 'No. We start with the business context and available evidence. Products or managed services are recommended only when the assessment and risk-treatment decision support them, with provider roles and incentives disclosed.'],
  ['Does CISO Operator run a 24/7 security operations center?', 'CISO Operator can architect, implement, and govern managed detection and response capabilities. When continuous monitoring or response is delivered by a specialist provider, that responsibility is stated clearly.'],
  ['Can you help secure AI agents?', 'Yes. The Secure AI & Agent Defense practice addresses inventory, data access, identity, tool permissions, prompt injection, action controls, human approval, logging, rollback, and incident readiness.'],
  ['Who performs the work?', 'Every engagement has a named senior cybersecurity lead. Depending on scope, CISO Operator can add certified security professionals and specialist providers while maintaining one operating method, clear responsibilities, and accountable oversight.'],
] as const;

function Faq() {
  return (
    <section data-sc-act="flow" className="section faq">
      <div className="shell faq-layout">
        <div><div className="eyebrow"><span /> Common questions</div><h2>Clarity before commitment.</h2></div>
        <div className="faq-list">
          {faqs.map(([question, answer], index) => (
            <details key={question} open={index === 0}><summary>{question}<ChevronDown size={18} /></summary><p>{answer}</p></details>
          ))}
        </div>
      </div>
    </section>
  );
}

const socialProfiles = [
  ['LinkedIn', 'https://www.linkedin.com/company/ciso-operator'],
  ['YouTube', 'https://www.youtube.com/@CISOOperator'],
  ['X', 'https://x.com/cisooperator'],
  ['TikTok', 'https://www.tiktok.com/@cisooperator'],
] as const;

function Footer() {
  return (
    <footer className="footer">
      <div className="shell footer__main">
        <div><Logo inverse /><p>Cybersecurity strategy becomes valuable when the business can operate it.</p></div>
        <div><span>Explore</span>{nav.map(([label, href]) => <a href={href} key={href}>{label}</a>)}</div>
        <div><span>Contact</span><a href={`mailto:${EMAIL}`}>{EMAIL}</a><a href="/#contact">Request a security call</a></div>
        <div><span>Follow</span>{socialProfiles.map(([label, href]) => <a href={href} key={href} target="_blank" rel="me noopener">{label}</a>)}</div>
      </div>
      <div className="shell footer__bottom"><span>© {new Date().getFullYear()} CISO Operator. All rights reserved.</span><div><a href="/privacy">Privacy Policy</a><a href="/terms">Terms</a><a href="/secure-contact">Secure Contact</a></div></div>
    </footer>
  );
}

function LegalPage({ type }: { type: 'privacy' | 'terms' }) {
  const privacy = type === 'privacy';
  usePageMetadata(
    privacy ? 'Privacy Policy | CISO Operator' : 'Website Terms | CISO Operator',
    privacy ? 'How CISO Operator handles personal information and works to keep its technologies compliant with applicable privacy laws and regulations.' : 'Terms governing use of the CISO Operator website and its informational content.',
    privacy ? '/privacy' : '/terms',
  );
  return (
    <div className="legal-page legal-page--polished">
      <div className="legal-nav shell"><Logo /><a className="text-link" href="/">Return to site <ArrowRight size={15} /></a></div>
      <main className="legal-content shell">
        <div className="eyebrow"><span /> {privacy ? 'Last updated September 15, 2026' : 'Effective August 24, 2026'}</div>
        <h1>{privacy ? 'Privacy Policy' : 'Website terms'}</h1>
        {privacy ? <>
          <p>CISO Operator is committed to handling business inquiries and website information with care. This website uses Google Analytics to understand site usage, Cloudflare services to deliver, protect, and process website inquiries, and ClickCease by CHEQ on qualifying paid-ad landing visits to help identify invalid advertising traffic.</p>
          <h2>Technology and privacy compliance</h2>
          <p>CISO Operator is committed to ensuring that the technologies we use and the technology services we provide remain compliant with relevant privacy laws and regulations applicable to our activities.</p>
          <p>This commitment guides our technology selection, configuration, maintenance, and review, including how personal information is collected, used, accessed, protected, retained, and deleted. We work to update our practices and safeguards as applicable requirements and technologies change.</p>
          <h2>Website analytics</h2><p>Google Analytics may collect information about visits and interactions, including pages viewed, device and browser information, approximate location, referral source, and identifiers used to distinguish visits. We use this information to understand website performance and improve the experience. Browser settings, extensions, and applicable consent choices may limit this collection.</p>
          <h2>Paid-traffic protection</h2><p>When a visitor reaches an advertising landing page through a qualifying Google Ads click, ClickCease may process the IP address, approximate location, advertising click identifier, landing and referral URLs, browser and device characteristics, a device fingerprint, and interaction or session signals such as scrolling and pointer activity. We use these signals to detect invalid or fraudulent traffic and help manage advertising exclusions. The inquiry form is excluded from our session-recording configuration, and ClickCease is not loaded on lead-confirmation pages. See the <a href="https://www.clickcease.com/privacy.html" rel="noreferrer">ClickCease privacy policy</a> for additional information.</p>
          <h2>Information you choose to send</h2><p>When you submit a contact form, we collect the name, work email, company, and business context you provide, along with the landing page and available campaign-attribution information. Cloudflare processes and stores the submission and securely forwards an operational copy to a restricted Google Workspace CRM spreadsheet so we can receive, protect, and respond to the inquiry. The site attempts to send an email notification to {EMAIL}; a successful form response confirms secure receipt by the site, not mailbox or CRM delivery. An inquiry is not automatically eligible for advertising audience use; any Customer Match eligibility requires applicable consent and internal review. We retain inquiry information only as reasonably necessary for business follow-up, security, recordkeeping, and applicable legal obligations.</p>
          <h2>Technical information</h2><p>Cloudflare may process standard request information—such as IP address, browser details, requested pages, and security events—to deliver, protect, and operate this website. That processing is governed by Cloudflare’s applicable terms and privacy commitments.</p>
          <h2>Do not send sensitive information</h2><p>Do not submit passwords, authentication secrets, regulated records, vulnerability details, or other confidential security information through the website or an unsolicited email. We will establish an appropriate secure channel if an engagement requires sensitive information.</p>
          <h2>Your choices</h2><p>You may contact us to ask about, correct, or request deletion of personal information you have provided through this website or by email, subject to applicable law and legal and operational retention requirements.</p>
          <h2>Changes to this policy</h2><p>We may update this Privacy Policy to reflect changes in our technologies, practices, or applicable requirements. The date above identifies the latest update.</p>
        </> : <>
          <p>These terms govern your use of the CISO Operator website. By using the site, you agree to use it lawfully and not interfere with its operation or security.</p>
          <h2>Informational content</h2><p>Website content is general information, not legal advice, an audit opinion, a security guarantee, or a substitute for an engagement tailored to your organization.</p>
          <h2>No client relationship</h2><p>Viewing the site, sending an inquiry, or participating in an introductory conversation does not create a client, fiduciary, or confidential advisory relationship. A relationship begins only through an executed written agreement.</p>
          <h2>No guarantee</h2><p>No security program can eliminate all risk. Outcomes depend on scope, authority, client decisions, implementation, third-party systems, and changing threats.</p>
          <h2>Third-party services</h2><p>Links and references to third parties do not imply endorsement, partnership, or responsibility for their services unless expressly stated in a written agreement.</p>
        </>}
        <h2>Contact</h2><p>Questions may be sent to <a href={`mailto:${EMAIL}`}>{EMAIL}</a>.</p>
        {!privacy && <p className="legal-note">These initial website policies should be reviewed by qualified counsel before broader commercial launch.</p>}
      </main>
      <Footer />
    </div>
  );
}

// The PGP fingerprint is supplied by the site owner; never derive it from the
// published key file itself.
const PGP_KEY_PATH = '/pgp/cisooperator.asc';
const PGP_FINGERPRINT = '125D FDD6 7100 8552 B63D 1330 BE43 37FD 2C04 D5E2';

function SecureContactPage() {
  usePageMetadata(
    'Secure Contact | CISO Operator',
    'How to reach CISO Operator securely about a security incident, an account takeover or other sensitive matters.',
    '/secure-contact',
  );
  return (
    <div className="legal-page legal-page--polished secure-contact-page">
      <div className="legal-nav shell"><Logo /><a className="text-link" href="/">Return to site <ArrowRight size={15} /></a></div>
      <main className="legal-content shell">
        <h1>Secure contact</h1>
        <p>If you need to reach us about something sensitive, such as an active security incident, a hijacked account or a threat against your organization, please use one of the channels below.</p>
        <h2>Email</h2>
        <p>General questions: <a href={`mailto:${EMAIL}`}>{EMAIL}</a></p>
        <p>Engagements and proposals: <a href="mailto:chinedu@cisooperator.com">chinedu@cisooperator.com</a></p>
        <p>Regular email is not encrypted end to end. Please don't send sensitive details by regular email. Use encrypted email instead.</p>
        <h2>Encrypted email (PGP)</h2>
        <p>Public key for chinedu@cisooperator.com: <a href={PGP_KEY_PATH} download="cisooperator.asc">Download key</a></p>
        <p>Fingerprint: <code className="secure-contact__fingerprint">{PGP_FINGERPRINT}</code></p>
        <p>Please check the fingerprint before you use the key.</p>
        <h2>Before you contact us</h2>
        <ul>
          <li>Never send passwords, two-step verification codes or recovery codes. We will never ask for them.</li>
          <li>If you think a device or account has been compromised, contact us from a different device or account if you can.</li>
          <li>For an active incident, start your message with URGENT and tell us the best way to reach you.</li>
        </ul>
        <h2>How we handle what you share</h2>
        <ul>
          <li>We ask only for the information we need.</li>
          <li>We don't share it with anyone without your permission.</li>
          <li>We delete case information when our work is finished, unless you ask us to keep it.</li>
        </ul>
        <h2>When we reply</h2>
        <p>We aim to reply within one business day (Monday to Friday, U.S. Eastern Time). We check messages marked URGENT sooner, including on weekends.</p>
        <h2>If you are in immediate danger</h2>
        <p>If anyone's physical safety is at risk, contact local emergency services first. Journalists, activists and civil-society groups facing a digital attack can also contact Access Now's Digital Security Helpline, which responds around the clock in several languages: <a href="https://www.accessnow.org/help/" target="_blank" rel="noopener noreferrer">https://www.accessnow.org/help/</a></p>
        <h2>Reporting a vulnerability in our website</h2>
        <p>If you have found a security issue on cisooperator.com, email <a href={`mailto:${EMAIL}`}>{EMAIL}</a>. Our security.txt file is at <a href="https://cisooperator.com/.well-known/security.txt">https://cisooperator.com/.well-known/security.txt</a></p>
      </main>
      <Footer />
    </div>
  );
}

function CraftHome() {
  const page = useRef<HTMLDivElement>(null);
  usePageImmersion(page);
  useEffect(() => {
    let disposed = false;
    const mount = () => {
      if (disposed || document.documentElement.dataset.craftMounted) return;
      const runtime = (window as unknown as { ScrollCraft?: { mount: (root: HTMLElement) => void } }).ScrollCraft;
      if (runtime) { runtime.mount(document.querySelector('.craft-home') as HTMLElement); document.documentElement.dataset.craftMounted = 'true'; }
    };
    let script = document.getElementById('scrollcraft-runtime') as HTMLScriptElement | null;
    if (!script) { script = document.createElement('script'); script.id = 'scrollcraft-runtime'; script.src = '/scrollcraft/scrollcraft.js'; document.body.appendChild(script); }
    script.addEventListener('load', mount);
    mount();
    return () => { disposed = true; script?.removeEventListener('load', mount); };
  }, []);
  return <div ref={page} className="craft-home craft-home--immersive craft-home--complete"><a className="craft-skip" href="#main-content">Skip to content</a><ImmersiveHero header={<Header />} /><main id="main-content"><Services /><Method /><AiDefense /><Audience /><About /><InsightsTeaser /><Faq /><Contact /></main><Footer /></div>;
}

export default function App({ path: pathProp }: { path?: string } = {}) {
  const path = (pathProp ?? window.location.pathname).replace(/\/$/, '') || '/';
  useEffect(() => {
    attributionFromUrl();
  }, []);
  if (path === '/insights') return <InsightsPage />;
  if (path.startsWith('/insights/')) {
    const article = articles.find((entry) => `/insights/${entry.slug}` === path);
    return article ? <ArticlePage article={article} /> : <InsightsPage />;
  }
  if (path === '/privacy') return <LegalPage type="privacy" />;
  if (path === '/terms') return <LegalPage type="terms" />;
  if (path === '/secure-contact') return <SecureContactPage />;
  if (path === '/cybersecurity-risk-assessment') return <ServiceLandingPage kind="risk" />;
  if (path === '/fractional-ciso') return <ServiceLandingPage kind="fractional" />;
  if (path === '/ai-agent-security') return <ServiceLandingPage kind="ai" />;
  if (path === '/thank-you-risk-assessment') return <ThankYouPage service="risk" />;
  if (path === '/thank-you-fractional-ciso') return <ThankYouPage service="fractional" />;
  if (path === '/thank-you-ai-agent-security') return <ThankYouPage service="ai" />;
  if (path === '/thank-you') return <ThankYouPage service="general" />;

  return <CraftHome />;
}
